/**
 * NsectionB7Page
 * B.7.政策、選項與實施計畫符合性說明
 * （A6.4-FORM-METH-002 Section 7. Demonstration of alignment with NDC/LT-LEDS）
 * 單一富文本欄位，段落 12 固定文字作為預設值
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB7Service, { NsectionB7Detail } from '../services/nsectionB7Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

const DEFAULT_CONTENT_EN = '<p>Activity participants shall provide to the DOE responsible to perform the validation of the Article 6.4 project an assessment, undertaken by the DNA of the host Party, of the activity\'s consistency with Decision 3/CMA.3 paragraph 40 (c) and paragraph 27 (a) as part of the host Party\'s approval.</p>';
const DEFAULT_CONTENT_ZH = '<p>活動參與者應向負責執行 Article 6.4 專案確認的指定作業實體(DOE)提供由地主國指定國家主管機關(DNA)進行的評估，以確認該活動與第 3/CMA.3 號決議第 40(c)段及第 27(a)段的一致性，作為地主國核准的一部分。</p>';

const NsectionB7Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB7Detail | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB7Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setContent('');
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB7Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setContent(result.alignment_content || '');
        } else {
          setData(null);
          // 首次進入無資料，帶入預設值
          setContent(isZh ? DEFAULT_CONTENT_ZH : DEFAULT_CONTENT_EN);
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        } else {
          // 404 也帶入預設值
          setContent(isZh ? DEFAULT_CONTENT_ZH : DEFAULT_CONTENT_EN);
        }
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const result = await nsectionB7Service.update(selectedProposalId, { alignment_content: content });
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b7', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_b7', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b7" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div style={{
          width: '100%', background: '#fff', borderRadius: '10px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
        }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#333', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
            {isZh ? '符合性說明' : 'Alignment Demonstration'}
          </div>

          {canUpdate ? (
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder={isZh ? '請輸入符合性說明內容...' : 'Enter alignment demonstration content...'}
              minHeight="300px"
            />
          ) : (
            <div
              style={{ padding: '16px', fontSize: '15px', color: '#333', lineHeight: 1.8, minHeight: '300px' }}
              dangerouslySetInnerHTML={{ __html: content || '-' }}
            />
          )}

          {canUpdate && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '16px' }}>
              <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NsectionB7Page;
